import { withSlotAreas } from './holeArea';
import { slotRuns } from './holeSlots';
import { picturedViews } from './jointShots';
import { useEffect, useMemo } from 'react';
import { CALL_INSET, PromptPanel, usePromptCall } from './PromptBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { useAssemblySteps } from './categoryBands';
import { useCategories } from './catalogCategories';
import { STEPS_MODEL } from './models';
import { usePartsRun } from './partsRun';
import { namedIn, withLetter } from './partsCatalogue';
import { HOLES_PHASE, STEPS_PHASE, useWorkingElsewhere } from './phaseWorking';
import { buildStepHolesPrompt, unmeasuredIn } from './stepHolesPrompt';
import { useHoleGroups, useMeshOf, type HoleGroup } from './holeGroups';
import {
  borelessHoles, drillingGroups, groupKey, holesBefore, holesContextFor,
  unmatchedDrilling, unplacedHoles, useStepHoles,
} from './stepHolesRun';
import { matchingRows, tableOutdated, useHardwareInteractions } from './hardwareInteractionsRun';
import { useStepSections } from './stepSections';
import { useStepsRun, type SubStep } from './stepsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// FIND THE HOLES — the call that reads, off each step's gestures, which holes the hardware goes
// into, in which part and where. In the Component parts subphase of Phase 6, where those holes
// are drilled: found beside the part they are drilled into, not three phases before it.
//
// IT STILL READS PHASE 4'S ANALYSIS, and cannot run before it: which gesture does what is that
// phase's answer, and this call is asked only where the hardware goes IN. A call of its own
// because /step-analyze invents the piece ids, so re-asking it to fix one hole would renumber
// every piece in the step. What it found is the table under the part it is drilled in (see
// HolesTable, drawn beside that part's mesh).
//
// IN PIECES, all reading useHoles: its prompt row, which the Holes & covers badge shows for the
// joint it walks to (see JointsRun), and what the last run could not place (FindHolesNotes), over the holes themselves.

// What both pieces read: the steps, their gestures, the holes found, and the context a gesture's
// call is built from.
export function useHoles(docId: string) {
  const { steps } = useAssemblySteps(docId);
  const st = useStepsRun(docId);
  const { items } = usePartsRun(docId);
  const holes = useStepHoles(docId);
  // Subscribed so the prompt and the warnings rebuild once the ontology lands: the builders
  // read it through the module cache, which answers "component" for everything until then.
  const cats = useCategories();
  // EACH PART'S 3D MODEL — what the call is shown of the parts it drills (see useMeshOf).
  const meshOf = useMeshOf(docId);
  // THE HARDWARE TABLE'S ROWS, which say which parts each piece of hardware goes through — see
  // drillGroupParts. Read by the grouping, the labels and the call alike.
  const table = useHardwareInteractions(docId);
  const interactions = useMemo(() => matchingRows(table, steps, st.subSteps), [table, steps, st.subSteps]);
  // THE TABLE IS THE AUTHORITY on which hardware goes into what (see drillGroupKey), so it has
  // to exist and to describe the sub-steps on screen: none, or one built from sub-steps since
  // re-analysed, and nothing is looked for.
  const noTable = !table.asked;
  const outdated = tableOutdated(table, steps, st.subSteps);
  // EVERY GROUP THE CALL IS SENT FOR, in manual order — which is also what the run menu offers,
  // one by one or all, and what the joint view tabs (see holeGroups).
  const gestures = useHoleGroups(docId);

  // What this gesture's call would be told, with the holes its own step declared before it read
  // from what is stored — as a run in order would have them.
  const contextAt = (g: { i: number; g: number; group: SubStep[] }) => {
    const all = drillingGroups(steps, st.subSteps, items, g.i, interactions);
    const letters = g.group.map((sub) => sub.letter);
    const stored = holes.byStep[steps[g.i].category];
    return holesContextFor(
      docId, steps, st.subSteps, items, holes.byStep, g.i, letters,
      holesBefore(stored, all, g.g), meshOf,
      (stored?.holes ?? []).filter((h) => !letters.includes(h.at)),
      interactions,
    );
  };
  return { steps, st, items, holes, cats, meshOf, interactions, gestures, contextAt, noTable, outdated };
}

// WHETHER FIND HOLES MAY RUN NOW, and where not, why — said rather than leaving a dead press.
// Nothing to read the holes off until a step has been broken into gestures, and not while the
// analysis that makes them is running — here or in another tab (see phaseWorking); nor without a
// hardware table that describes the sub-steps on screen.
export function useCanFind(docId: string, locked?: boolean): { canFind: boolean; why: string | null } {
  const { st, holes, noTable, outdated, gestures } = useHoles(docId);
  const elsewhere = useWorkingElsewhere(docId);
  const doneSteps = Object.values(st.subSteps).some((a) => a.length > 0);
  const analysing = st.busy || elsewhere?.phase === STEPS_PHASE;
  const why = noTable ? 'notice.holes.needTable' : outdated ? 'notice.holes.tableOutdated'
    : !doneSteps || !gestures.length ? 'notice.holes.needSteps' : null;
  return {
    canFind: !why && !analysing && !holes.busy && !locked && elsewhere?.phase !== HOLES_PHASE,
    why,
  };
}

// ONE JOINT'S FIND HOLES PROMPT, as the call sends it — the pages, and the renders of its parts
// made on demand by the function the run makes them with (see picturedViews) — with its stored
// answer.
function useJointHoles(docId: string, file: Blob, joint: HoleGroup) {
  const { t } = useLanguage();
  const { steps, st, items, holes, cats, meshOf, interactions, contextAt } = useHoles(docId);
  const sections = useStepSections(file, steps);
  const step = steps[joint.i];
  // THE KEY IT IS ASKED UNDER NOW — its step and its gestures' letters — and THE ONE ITS ANSWER IS
  // FILED UNDER, which is that one unless Phase 4 has run again since: the letters the answer was
  // filed by are then another analysis's, and the window showed a prompt with no reply. Found
  // again by what the reply is about: the stored answer of this step whose own text names the
  // most of this joint's holes (H203-A…).
  const live = step ? groupKey(step.category, joint.group) : '';
  const key = useMemo(() => {
    if (!step || holes.raw[live] != null || holes.sent[live] || holes.running.includes(live)) return live;
    const ids = [...new Set(joint.rows.flatMap((r) => r.holes.flatMap((h) => h.ids ?? [])))];
    if (!ids.length) return live;
    const said = (k: string) => `${holes.raw[k] ?? ''}\n${holes.sent[k]?.prompt ?? ''}`;
    const named = (k: string) => ids.filter((id) => new RegExp(`\\b${id}\\b`).test(said(k))).length;
    const best = [...new Set([...Object.keys(holes.raw), ...Object.keys(holes.sent)])]
      .filter((k) => k.startsWith(`${step.category}#`))
      .map((k) => ({ k, n: named(k) }))
      .sort((a, b) => b.n - a.n)[0];
    return best && best.n > 0 ? best.k : live;
  }, [step, live, joint.rows, holes.raw, holes.sent, holes.running]);
  const ctx = useMemo(
    () => contextAt(joint),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [docId, steps, st.subSteps, items, holes.byStep, joint, cats, meshOf, interactions],
  );
  // WHAT THE LAST RUN SENT, where there was one and it is not being asked again: its own text, and
  // the pictures drawn again — as many as went (see SentRecord).
  const sent = holes.running.includes(live) ? undefined : holes.sent[key];
  const load = async () => {
    const pages = sections[step?.category ?? ''] ?? [];
    if (sent) {
      // AND THE ANSWER ON THEM: where each hole it placed was drilled, crossed on its face.
      const drilled = holes.byStep[step?.category ?? '']?.holes ?? [];
      const drawn = ctx && sent.pictures
        ? await picturedViews(await withSlotAreas(ctx, meshOf, undefined, undefined, true), meshOf, true, drilled) : { views: [] };
      return { prompt: sent.prompt, urls: [...pages, ...drawn.views.slice(0, sent.pictures)], model: STEPS_MODEL };
    }
    if (!ctx) return { prompt: '', urls: pages, model: STEPS_MODEL };
    // AS A RE-RUN WOULD ASK IT: on the parts undrilled, which is how the run finds them.
    const asked = await withSlotAreas(ctx, meshOf, undefined, undefined, true);
    const drawn = await picturedViews(asked, meshOf, true);
    return {
      prompt: buildStepHolesPrompt({ ...asked, pictured: drawn.shown }),
      urls: [...pages, ...drawn.views],
      model: STEPS_MODEL,
    };
  };
  // WHY THERE IS NO REASONING, where OpenAI said how much it thought: a summary is only written
  // where there was thinking to summarise, and "none available" read as something withheld.
  const thought = holes.thinking[key];
  const noThoughts = thought === 0
    ? t('notice.holes.noThinking')
    : typeof thought === 'number'
      ? t('notice.holes.thoughtUnsummarised').replace('{n}', String(thought))
      : undefined;
  // WHAT THE PROMPT IS BUILT FROM, beyond the joint: its pages, the parts with a model to picture,
  // and which holes have a measured patch. The meshes and the pages arrive after the badge can be
  // opened, and a panel reloaded only on a new joint kept its first, emptier question.
  const revision = `${key}|${(sections[step?.category ?? ''] ?? []).length}|${ctx?.drilled.filter((id) => !!meshOf(id)).join(',') ?? ''}|${
    ctx?.slots.map((sl) => (sl.patch ? 'p' : '-')).join('') ?? ''}|${sent ? sent.prompt.length : ''}`;
  // HOW MANY PICTURES THE CALL HAS, known before they are drawn: the step's pages, and two per run —
  // or as many as went, for a run that answered. A spinning tile each while they are made.
  const pages = (sections[step?.category ?? ''] ?? []).length;
  const pending = pages + (sent ? sent.pictures : ctx ? 2 * slotRuns(ctx.slots).length : 0);
  return {
    key, revision, load, pending, noThoughts, busy: holes.running.includes(live),
    raw: holes.raw[key], reasoning: holes.reasoning[key] ?? '',
  };
}

// THE CALL'S ROW: the prompt, and the reply AS IT CAME, not what was kept — the route
// drops a hole missing its face, silently, and the only way to see which is to read the two side
// by side.
export function HolesFirstPanel({ docId, file, joint }: { docId: string; file: Blob; joint: HoleGroup }) {
  const { t } = useLanguage();
  const one = useJointHoles(docId, file, joint);
  const call = usePromptCall({ load: one.load, revision: one.revision });
  useEffect(() => {
    call.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <PromptPanel label={t('notice.tip.menuPrompt')} call={call} pending={one.pending} />
      {one.raw && !one.busy && (
        <div className="mt-6 border-t border-slate-200 pt-4">
          <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t('notice.cat.reasoningTitle')}
          </p>
          <div className={CALL_INSET}>{reasoningTooltip(one.reasoning, one.raw, t, null, false, one.noThoughts)}</div>
        </div>
      )}
    </>
  );
}

// WHAT THE LAST RUN COULD NOT PLACE, over the holes in the Component parts subphase — where the
// holes are read, and where a missing one would otherwise just be missing.
export function FindHolesNotes({ docId }: { docId: string }) {
  const { t, language } = useLanguage();
  const { steps, st, items, holes, cats, interactions, gestures, contextAt, noTable } = useHoles(docId);
  // THE GESTURES NO ROW OF THE TABLE NAMES, which are not looked at — see unmatchedDrilling.
  const unmatched = useMemo(
    () => (noTable ? [] : unmatchedDrilling(steps, st.subSteps, items, interactions)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noTable, steps, st.subSteps, items, interactions, cats],
  );
  const nameOf = (id: string) => {
    const p = items[Number(id)];
    return p ? withLetter(namedIn(p, language) || `#${id}`, p.letter) : `#${id}`;
  };
  // THE PARTS THIS MANUAL BORES INTO THAT NOTHING HAS MEASURED — see unmeasuredIn. Over every
  // gesture the call is sent for: a part with no ruler is a fact about the manual.
  const unmeasured = useMemo(() => {
    const out = new Map<string, string>();
    for (const g of gestures) {
      const ctx = contextAt(g);
      if (ctx) for (const [id, name] of unmeasuredIn(ctx)) out.set(id, name);
    }
    return [...out.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, steps, st.subSteps, items, holes.byStep, gestures, cats, interactions]);
  // AND THE BORES THAT LANDED ON NO PLANK, and the ones answered for hardware that makes no
  // hole — see unplacedHoles and borelessHoles.
  const unplaced = useMemo(() => unplacedHoles(holes.byStep, items), [holes.byStep, items]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const boreless = useMemo(() => borelessHoles(holes.byStep, items), [holes.byStep, items, cats]);

  // NOT ITS ERROR: that is said under the Holes & covers badge, beside the run that failed.
  if (!boreless.length && !unplaced.length && !unmeasured.length && !unmatched.length) return null;
  return (
    <div className="flex flex-col items-center gap-3">
      {/* NOT AN ERROR, AND NOT NOTHING: the call answered, and some of what it answered cannot
          be turned into a bore in a plank. Amber, and it stays after a run that succeeded. */}
      {boreless.length > 0 && (
        <p className="text-center text-sm text-amber-600">
          {t('notice.holes.noBoreCount').replace('{n}', String(boreless.length))}
        </p>
      )}
      {unplaced.length > 0 && (
        <p className="text-center text-sm text-amber-600">
          {t('notice.holes.noPartCount').replace('{n}', String(unplaced.length))}
        </p>
      )}
      {unmatched.length > 0 && (
        <p className="text-center text-sm text-amber-600">
          {t('notice.holes.notInTable').replace('{list}', unmatched
            .map((u) => `${u.label} – ${u.bores.map(nameOf).join(', ')}`).join('; '))}
        </p>
      )}
      {unmeasured.length > 0 && (
        <p className="text-center text-sm text-amber-600">
          {t('notice.holes.unmeasured').replace('{parts}', unmeasured.join(', '))}
        </p>
      )}
    </div>
  );
}

